<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Textarea from '@/components/ui/Textarea.vue'
import Label from '@/components/ui/Label.vue'
import Select from '@/components/ui/Select.vue'
import Checkbox from '@/components/ui/Checkbox.vue'
import Alert from '@/components/ui/Alert.vue'

const COPY_SUCCESS_DURATION = 2000
const COPY_ERROR_DURATION = 3000

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const { t } = useI18n()

const companyName = process.env.VUE_APP_COMPANY_NAME || 'Scytale'

const title = ref('')
const secretContent = ref('')
const burnAfterRead = ref(false)
const expiryOption = ref('1h')

const mainBlock = ref('public')
const protectionType = ref('link_only')

const additionalPassword = ref('')
const showAdditionalPassword = ref(false)
const recipientEmail = ref('')

const response = ref(null)
const error = ref(null)
const copyToast = ref(false)
const isLoading = ref(false)
const savedPreview = ref(null)

const isEmailInvalid = computed(() => {
  return recipientEmail.value && !emailRegex.test(recipientEmail.value)
})

const passwordStrengthScore = computed(() => {
  const p = additionalPassword.value
  if (!p) return 0
  let score = 0
  if (p.length >= 8) score++
  if (p.length >= 12) score++
  if (/[A-Z]/.test(p)) score++
  if (/[a-z]/.test(p)) score++
  if (/[0-9]/.test(p)) score++
  if (/[^A-Za-z0-9]/.test(p)) score++
  return score
})

const passwordStrengthLevel = computed(() => {
  if (!additionalPassword.value) return null
  const s = passwordStrengthScore.value
  if (s <= 2) return 'weak'
  if (s <= 4) return 'medium'
  return 'strong'
})

const isPasswordValid = computed(() => additionalPassword.value.length >= 8)

const strengthBarClass = (bar) => {
  if (!additionalPassword.value) return 'bg-slate-200 dark:bg-slate-700'
  const level = passwordStrengthLevel.value
  if (bar === 1) {
    if (level === 'weak') return 'bg-red-400'
    if (level === 'medium') return 'bg-yellow-400'
    return 'bg-green-400'
  }
  if (bar === 2) {
    if (level === 'medium') return 'bg-yellow-400'
    if (level === 'strong') return 'bg-green-400'
    return 'bg-slate-200 dark:bg-slate-700'
  }
  return level === 'strong' ? 'bg-green-400' : 'bg-slate-200 dark:bg-slate-700'
}

const passwordStrengthLabel = computed(() => {
  const level = passwordStrengthLevel.value
  if (!level) return ''
  return t(`form.passwordStrength${level.charAt(0).toUpperCase() + level.slice(1)}`)
})

watch(protectionType, () => {
  additionalPassword.value = ''
  recipientEmail.value = ''
  error.value = null
})

// Reset protectionType when switching between public and organisation
watch(mainBlock, (newVal) => {
  if (newVal === 'organisation' && protectionType.value === 'email_code') {
    // Organisation doesn't support email_code, reset to link_only
    protectionType.value = 'link_only'
  }
  additionalPassword.value = ''
  recipientEmail.value = ''
  error.value = null
})

const resetFormInputs = () => {
  title.value = ''
  secretContent.value = ''
  mainBlock.value = 'public'
  protectionType.value = 'link_only'
  additionalPassword.value = ''
  recipientEmail.value = ''
  expiryOption.value = '1h'
  burnAfterRead.value = false
}

const createPassword = async () => {
  error.value = null
  response.value = null
  isLoading.value = true

  if (!secretContent.value) {
    error.value = t('errors.secretEmpty')
    isLoading.value = false
    return
  }
  if (protectionType.value === 'link_pass' && !additionalPassword.value) {
    error.value = t('errors.passwordRequired')
    isLoading.value = false
    return
  }
  if (protectionType.value === 'link_pass' && !isPasswordValid.value) {
    error.value = t('errors.passwordTooShort')
    isLoading.value = false
    return
  }

  if (mainBlock.value === 'public' && protectionType.value === 'email_code') {
    if (!recipientEmail.value) {
      error.value = t('errors.emailRequired')
      isLoading.value = false
      return
    }

    if (isEmailInvalid.value) {
      error.value = t('errors.emailInvalid')
      isLoading.value = false
      return
    }
  }

  const requestBody = {
    title: title.value || 'Untitled Secret',
    content: secretContent.value,
    expiry_option: expiryOption.value,
    burn_after_read: burnAfterRead.value,
    block_type: mainBlock.value,
    protection_type: protectionType.value,
    password: additionalPassword.value || null,
    recipient_email: recipientEmail.value || null,
    frontend_origin: window.location.origin
  }

  try {
    const res = await fetch(`/api/passwords/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      credentials: 'include'
    })

    if (!res.ok) {
      const errData = await res.json()
      throw new Error(errData.detail || t('errors.generic'))
    }
    response.value = await res.json()

    savedPreview.value = {
      title: title.value,
      mainBlock: mainBlock.value,
      protectionType: protectionType.value,
      duration: previewDuration.value,
      burn: burnAfterRead.value
    }
    resetFormInputs()
  } catch (e) {
    if (!error.value) error.value = e.message
  } finally {
    isLoading.value = false
  }
}

const copyLink = () => {
  if (response.value?.share_link) {
    navigator.clipboard.writeText(response.value.share_link)
      .then(() => {
        copyToast.value = true
        setTimeout(() => { copyToast.value = false }, COPY_SUCCESS_DURATION)
      })
      .catch(() => {
        copyToast.value = true
        setTimeout(() => { copyToast.value = false }, COPY_ERROR_DURATION)
      })
  }
}

const getRadioLabelClass = (isActive) => {
  return [
    'block w-full cursor-pointer rounded-md text-center text-xs md:text-sm py-2 px-1 md:px-4 transition-colors leading-tight',
    isActive
      ? 'bg-white/10 text-foreground shadow-md'
      : 'text-muted-foreground hover:text-foreground'
  ]
}

// Preview computed values
const previewDuration = computed(() => {
  if (expiryOption.value === '1h') return t('form.duration1h')
  if (expiryOption.value === '5h') return t('form.duration5h')
  if (expiryOption.value === '1d') return t('form.duration1d')
  if (expiryOption.value === '7d') return t('form.duration7d')
  return expiryOption.value
})

const buildDescriptionHtml = (mb, pt, duration, burn) => {
  const b = (text) => `<strong>${text}</strong>`
  const lines = []

  if (mb === 'organisation') {
    lines.push(`${b(t('form.descOrgBold'))} ${t('form.descOrgSuffix')}`)
  } else {
    lines.push(`${b(t('form.descPublicBold'))} ${t('form.descPublicSuffix')}`)
  }

  const protBold = pt === 'link_pass' ? t('form.descPasswordBold')
    : pt === 'email_code' ? t('form.descEmailBold')
    : t('form.descLinkOnlyBold')
  lines.push(`${t('form.descProtPrefix')} ${b(protBold)}${t('form.descProtSuffix')}`)

  lines.push(`${t('form.descExpiryPrefix')} ${b(duration)}${t('form.descExpirySuffix')}`)

  if (burn) lines.push(b(t('form.descBurn')))

  return lines.join('<br/>')
}

const previewDescription = computed(() =>
  buildDescriptionHtml(mainBlock.value, protectionType.value, previewDuration.value, burnAfterRead.value)
)

const savedDescription = computed(() =>
  savedPreview.value
    ? buildDescriptionHtml(savedPreview.value.mainBlock, savedPreview.value.protectionType, savedPreview.value.duration, savedPreview.value.burn)
    : ''
)
</script>

<template>
  <!-- Toast notification -->
  <transition name="toast">
    <div v-if="copyToast" class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-medium px-6 py-3 rounded shadow-xl flex items-center gap-2">
      <svg class="w-4 h-4 text-green-400 dark:text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>
      {{ $t('form.copySuccess') }}
    </div>
  </transition>

  <div class="flex flex-col md:flex-row overflow-hidden" style="height: 100vh;">
    <!-- LEFT PANEL: Dynamic preview — hidden on mobile -->
    <div class="hidden md:flex md:w-1/2 bg-slate-800 dark:bg-white flex-col justify-center px-12 overflow-y-auto" style="padding-top: calc(2.5rem + 2rem); padding-bottom: 2rem;">
      <transition name="slide-fade">
        <div v-if="response && savedPreview" class="space-y-8">
          <p class="text-xs font-semibold tracking-widest uppercase text-white/40 dark:text-slate-400">{{ companyName }}</p>
          <p class="text-3xl font-semibold text-white dark:text-slate-900 leading-tight">
            {{ savedPreview.title || $t('form.titlePlaceholder') }}
          </p>
          <p class="text-2xl text-white/60 dark:text-slate-500 leading-relaxed" v-html="savedDescription"></p>
        </div>

        <div v-else class="space-y-8">
          <p class="text-xs font-semibold tracking-widest uppercase text-white/40 dark:text-slate-400">{{ companyName }}</p>
          <p class="text-3xl font-semibold text-white dark:text-slate-900 leading-tight">
            {{ title || $t('form.titlePlaceholder') }}
          </p>
          <p class="text-2xl text-white/60 dark:text-slate-500 leading-relaxed" v-html="previewDescription"></p>
        </div>
      </transition>
    </div>

    <!-- RIGHT PANEL: Form inputs — full width on mobile -->
    <div class="flex-1 bg-white dark:bg-[#0f172a] flex flex-col px-5 md:px-12 overflow-y-auto" style="padding-top: calc(2.5rem + 2rem); padding-bottom: 2rem;">
      <!-- Success: centered link + copy, fixed bottom back button -->
      <div v-if="response" class="flex-1 flex flex-col">
        <div class="flex-1 flex flex-col items-center justify-center space-y-4 text-center">
          <div class="w-full max-w-sm bg-black/5 dark:bg-white/5 rounded p-3 break-all text-xs text-slate-600 dark:text-slate-300 font-mono">
            {{ response.share_link }}
          </div>
          <Button class="w-full max-w-sm h-10 btn-pill" @click="copyLink">{{ $t('form.copyButton') }}</Button>
        </div>
        <div class="pb-2">
          <Button class="w-full btn-pill" variant="outline" @click="response = null; savedPreview = null">
            {{ $t('form.createButton') }}
          </Button>
        </div>
      </div>

      <!-- Form -->
      <div v-else class="space-y-5">
        <div>
          <Label class="text-slate-900 dark:text-white">{{ $t('form.titleLabel') }}</Label>
          <Input v-model="title" :placeholder="$t('form.titlePlaceholder')" class="pill-input" />
        </div>

        <div>
          <Label class="text-slate-900 dark:text-white">{{ $t('form.secretLabel') }} <span class="text-destructive">*</span></Label>
          <Textarea v-model="secretContent" class="pill-input" :placeholder="$t('form.secretPlaceholder')" rows="5" />
        </div>

        <div>
          <Label class="text-slate-900 dark:text-white">{{ $t('form.methodLabel') }}</Label>
          <div class="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-black/5 dark:bg-white/5 p-1">
            <div class="relative">
              <input type="radio" v-model="mainBlock" value="public" id="block_public" class="peer sr-only" />
              <label for="block_public" :class="getRadioLabelClass(mainBlock === 'public')">{{ $t('form.methodPublic') }}</label>
            </div>

            <div class="relative">
              <input type="radio" v-model="mainBlock" value="organisation" id="block_org" class="peer sr-only" />
              <label for="block_org" :class="getRadioLabelClass(mainBlock === 'organisation')">{{ $t('form.methodOrg') }}</label>
            </div>
          </div>
        </div>

        <div>
          <div v-show="mainBlock === 'public'" class="soft-border p-4 space-y-4">
            <Label class="text-slate-900 dark:text-white">{{ $t('form.protectionLabel') }}</Label>

            <div class="grid grid-cols-3 gap-2 rounded-lg bg-black/5 dark:bg-white/5 p-1">
              <div class="relative">
                <input type="radio" v-model="protectionType" value="link_only" id="prot_public_link" class="peer sr-only" />
                <label for="prot_public_link" :class="getRadioLabelClass(protectionType === 'link_only')">{{ $t('form.protLinkOnly') }}</label>
              </div>

              <div class="relative">
                <input type="radio" v-model="protectionType" value="link_pass" id="prot_public_pass" class="peer sr-only" />
                <label for="prot_public_pass" :class="getRadioLabelClass(protectionType === 'link_pass')">{{ $t('form.protPassword') }}</label>
              </div>

              <div class="relative">
                <input type="radio" v-model="protectionType" value="email_code" id="prot_public_email" class="peer sr-only" />
                <label for="prot_public_email" :class="getRadioLabelClass(protectionType === 'email_code')">{{ $t('form.protEmail') }}</label>
              </div>
            </div>

            <div
              class="stable-slot overflow-hidden"
              :style="{ minHeight: protectionType === 'link_only' ? '0px' : '112px' }"
            >
              <div v-show="protectionType === 'link_pass'" key="pass">
                <Label class="text-slate-900 dark:text-white">{{ $t('form.addPassLabel') }} <span class="text-destructive">*</span></Label>
                <div class="relative">
                  <Input v-model="additionalPassword" class="pill-input pr-10" :type="showAdditionalPassword ? 'text' : 'password'" :placeholder="$t('form.addPassPlaceholder')" />
                  <button type="button" @click="showAdditionalPassword = !showAdditionalPassword" tabindex="-1"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none">
                    <svg v-if="!showAdditionalPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                    </svg>
                  </button>
                </div>
                <div v-if="additionalPassword" class="mt-1.5">
                  <div class="flex gap-1 mb-1">
                    <div class="h-1 flex-1 rounded-full transition-colors duration-200" :class="strengthBarClass(1)" />
                    <div class="h-1 flex-1 rounded-full transition-colors duration-200" :class="strengthBarClass(2)" />
                    <div class="h-1 flex-1 rounded-full transition-colors duration-200" :class="strengthBarClass(3)" />
                  </div>
                  <p class="text-xs transition-colors duration-200"
                    :class="{
                      'text-red-500 dark:text-red-400': passwordStrengthLevel === 'weak',
                      'text-yellow-600 dark:text-yellow-400': passwordStrengthLevel === 'medium',
                      'text-green-600 dark:text-green-400': passwordStrengthLevel === 'strong'
                    }"
                  >
                    {{ passwordStrengthLabel }}
                    <span v-if="!isPasswordValid" class="text-slate-400 dark:text-slate-500"> — {{ $t('form.passwordHint') }}</span>
                  </p>
                </div>
              </div>

              <div v-show="protectionType === 'email_code'" key="email">
                <Label class="text-slate-900 dark:text-white">{{ $t('form.emailLabel') }} <span class="text-destructive">*</span></Label>
                <Input v-model="recipientEmail" type="email" class="pill-input" :placeholder="$t('form.emailPlaceholderUser') + '@' + $t('form.emailPlaceholderDomain')"/>

                <p v-if="isEmailInvalid" class="hint text-destructive">
                  {{ $t('errors.emailInvalid') }}
                </p>

                <p v-else class="hint">
                  {{ $t('form.emailHint') }}
                </p>
              </div>
            </div>
          </div>

          <div v-show="mainBlock === 'organisation'" class="soft-border p-4 space-y-4">
            <div class="space-y-4">
              <p class="hint">{{ $t('form.orgHint') }}</p>
              <Label class="text-slate-900 dark:text-white">{{ $t('form.protectionLabel') }}</Label>

              <div class="grid grid-cols-2 gap-2 rounded-lg bg-black/5 dark:bg-white/5 p-1">
                <div class="relative">
                  <input type="radio" v-model="protectionType" value="link_only" id="prot_org_link" class="peer sr-only" />
                  <label for="prot_org_link" :class="getRadioLabelClass(protectionType === 'link_only')">{{ $t('form.protLinkOnly') }}</label>
                </div>

                <div class="relative">
                  <input type="radio" v-model="protectionType" value="link_pass" id="prot_org_pass" class="peer sr-only" />
                  <label for="prot_org_pass" :class="getRadioLabelClass(protectionType === 'link_pass')">{{ $t('form.protPassword') }}</label>
                </div>
              </div>

              <div
                class="stable-slot overflow-hidden"
                :style="{ minHeight: protectionType === 'link_pass' ? '112px' : '0px' }"
              >
                <div v-show="protectionType === 'link_pass'">
                  <Label class="text-slate-900 dark:text-white">{{ $t('form.addPassLabel') }} <span class="text-destructive">*</span></Label>
                  <div class="relative">
                    <Input v-model="additionalPassword" class="pill-input pr-10" :type="showAdditionalPassword ? 'text' : 'password'" :placeholder="$t('form.addPassPlaceholder')" />
                    <button type="button" @click="showAdditionalPassword = !showAdditionalPassword" tabindex="-1"
                      class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none">
                      <svg v-if="!showAdditionalPassword" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                      </svg>
                    </button>
                  </div>
                  <div v-if="additionalPassword" class="mt-1.5">
                    <div class="flex gap-1 mb-1">
                      <div class="h-1 flex-1 rounded-full transition-colors duration-200" :class="strengthBarClass(1)" />
                      <div class="h-1 flex-1 rounded-full transition-colors duration-200" :class="strengthBarClass(2)" />
                      <div class="h-1 flex-1 rounded-full transition-colors duration-200" :class="strengthBarClass(3)" />
                    </div>
                    <p class="text-xs transition-colors duration-200"
                      :class="{
                        'text-red-500 dark:text-red-400': passwordStrengthLevel === 'weak',
                        'text-yellow-600 dark:text-yellow-400': passwordStrengthLevel === 'medium',
                        'text-green-600 dark:text-green-400': passwordStrengthLevel === 'strong'
                      }"
                    >
                      {{ passwordStrengthLabel }}
                      <span v-if="!isPasswordValid" class="text-slate-400 dark:text-slate-500"> — {{ $t('form.passwordHint') }}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="grid gap-4 md:grid-cols-2 md:items-end">
          <div>
            <Label class="text-slate-900 dark:text-white">{{ $t('form.durationLabel') }}</Label>
            <Select v-model="expiryOption" class="pill-input">
              <option value="1h">{{ $t('form.duration1h') }}</option>
              <option value="5h">{{ $t('form.duration5h') }}</option>
              <option value="1d">{{ $t('form.duration1d') }}</option>
              <option value="7d">{{ $t('form.duration7d') }}</option>
            </Select>
          </div>
          <label class="flex items-center gap-2 pb-1 text-sm text-slate-900 dark:text-white">
            <Checkbox v-model="burnAfterRead" />
            {{ $t('form.burnLabel') }}
          </label>
        </div>

        <div>
          <Button class="w-full h-10 btn-pill" :disabled="isLoading" @click="createPassword">
            <svg v-if="isLoading" class="-ml-1 mr-2 h-4 w-4 animate-spin text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ isLoading ? $t('form.creatingButton') : $t('form.createButton') }}
          </Button>
        </div>

        <transition name="fade">
          <Alert v-if="error" type="error">{{ error }}</Alert>
        </transition>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-destructive {
  color: #ef4444;
}

.leading-relaxed :deep(strong),
.leading-relaxed strong {
  color: #ffffff;
  font-weight: 700;
}
.dark .leading-relaxed :deep(strong),
.dark .leading-relaxed strong {
  color: #0f172a;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.4s ease;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 16px);
}

.stable-slot {
  display: grid;
  transition: min-height 0.15s ease;
}
.stable-slot > *:first-child {
  margin-top: 0 !important;
}
.stable-slot > *:last-child {
  margin-bottom: 0 !important;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
